import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CardMembership as PlanIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useReceivablePaymentMethods } from '../../hooks/useReceivablePaymentMethods';
import { planInstallmentPreview } from '../../utils/patientPlanInstallments';
import {
  cloudApiDialogCancelButtonSx,
  cloudApiDialogContentSx,
  cloudApiDialogFooterSx,
  cloudApiDialogHeaderSx,
  cloudApiDialogHeaderIconSx,
  cloudApiDialogPaperSx,
  cloudApiDialogPrimaryButtonSx,
} from '../../pages/whatsappCloudApiTheme';

export type SellPlanDialogPatient = {
  id: string;
  full_name?: string;
};

type PlanProcedure = {
  id: string;
  name: string;
  plan_duration_options?: { months: number; price: number; label?: string | null }[];
  plan_allows_installments?: boolean;
  plan_sessions_per_cycle?: number | null;
  plan_weekly_frequency?: number | null;
  price?: number;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSold?: () => void;
  /** Quando informado (ex.: painel do paciente), o paciente fica fixo. */
  initialPatient?: SellPlanDialogPatient | null;
};

const formatCurrency = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
const todayIso = () => new Date().toISOString().slice(0, 10);

const SellPlanDialog: React.FC<Props> = ({
  open,
  onClose,
  onSold,
  initialPatient,
}) => {
  const { options: paymentOptions } = useReceivablePaymentMethods();
  const patientLocked = Boolean(initialPatient?.id);

  const [catalog, setCatalog] = useState<PlanProcedure[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [professionals, setProfessionals] = useState<any[]>([]);

  const [patient, setPatient] = useState<SellPlanDialogPatient | null>(initialPatient || null);
  const [patientOptions, setPatientOptions] = useState<any[]>([]);
  const [patientSearchLoading, setPatientSearchLoading] = useState(false);
  const patientSearchSeq = useRef(0);

  const [procedureId, setProcedureId] = useState('');
  const [durationMonths, setDurationMonths] = useState<number | ''>('');
  const [startDate, setStartDate] = useState(todayIso());
  const [professionalId, setProfessionalId] = useState('');
  const [paymentMode, setPaymentMode] = useState<'installments' | 'upfront'>('installments');
  const [paymentMethod, setPaymentMethod] = useState('dinheiro');
  const [autoRenew, setAutoRenew] = useState(false);
  const [prorateFirst, setProrateFirst] = useState(true);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedProcedure = useMemo(
    () => catalog.find((p) => p.id === procedureId) || null,
    [catalog, procedureId],
  );

  const durationOptions = useMemo(() => {
    const opts = selectedProcedure?.plan_duration_options || [];
    if (opts.length) return opts;
    if (!selectedProcedure) return [];
    return [1, 3, 6, 12].map((months) => ({
      months,
      price: money2(Number(selectedProcedure.price || 0) * months),
      label: null,
    }));
  }, [selectedProcedure]);

  const totalAmount = useMemo(() => {
    const months = Number(durationMonths) || 0;
    if (!months) return 0;
    const match = durationOptions.find((o) => o.months === months);
    return match?.price ?? 0;
  }, [durationMonths, durationOptions]);

  const allowsInstallments = selectedProcedure?.plan_allows_installments !== false;

  const installmentPreview = useMemo(() => {
    const months = Number(durationMonths) || 0;
    if (!months || !totalAmount || paymentMode !== 'installments') return [];
    return planInstallmentPreview(totalAmount, months, startDate, prorateFirst);
  }, [durationMonths, totalAmount, startDate, prorateFirst, paymentMode]);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const { data } = await axios.get(getApiUrl('/clinic/patient-plans/catalog'));
      setCatalog(data?.data || []);
    } catch {
      setCatalog([]);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  const loadProfessionals = useCallback(async () => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/professionals'), { params: { limit: 200, is_active: true } });
      setProfessionals(data?.data || []);
    } catch {
      setProfessionals([]);
    }
  }, []);

  const searchPatients = useCallback((q: string) => {
    const term = (q || '').trim();
    const seq = ++patientSearchSeq.current;
    setPatientSearchLoading(true);
    void axios
      .get(getApiUrl('/clinic/patients'), { params: { search: term, limit: 25 } })
      .then((res) => {
        if (seq !== patientSearchSeq.current) return;
        setPatientOptions(res.data?.data || []);
      })
      .catch(() => {
        if (seq !== patientSearchSeq.current) return;
        setPatientOptions([]);
      })
      .finally(() => {
        if (seq === patientSearchSeq.current) setPatientSearchLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setStartDate(todayIso());
    setProrateFirst(true);
    if (initialPatient?.id) {
      setPatient(initialPatient);
    }
    void loadCatalog();
    void loadProfessionals();
    if (!patientLocked) searchPatients('');
  }, [open, initialPatient, patientLocked, loadCatalog, loadProfessionals, searchPatients]);

  useEffect(() => {
    if (!open || !selectedProcedure) return;
    const first = durationOptions[0];
    if (first && !durationMonths) setDurationMonths(first.months);
    if (!allowsInstallments) setPaymentMode('upfront');
  }, [open, selectedProcedure, durationOptions, durationMonths, allowsInstallments]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);
    if (!patient?.id) {
      setError('Selecione o paciente.');
      return;
    }
    if (!procedureId) {
      setError('Selecione o plano.');
      return;
    }
    const months = Number(durationMonths);
    if (!months) {
      setError('Selecione a vigência.');
      return;
    }
    if (totalAmount <= 0) {
      setError('Configure o valor do plano no cadastro de procedimentos.');
      return;
    }

    setSaving(true);
    try {
      await axios.post(getApiUrl('/clinic/patient-plans'), {
        patient_id: patient.id,
        procedure_id: procedureId,
        duration_months: months,
        professional_id: professionalId || null,
        start_date: startDate,
        payment_mode: paymentMode,
        payment_method: paymentMethod || null,
        auto_renew: autoRenew,
        prorate_first_installment: prorateFirst,
        notes: notes.trim() || null,
      });
      onSold?.();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Não foi possível concluir a venda.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: cloudApiDialogPaperSx }}
    >
      <DialogTitle sx={cloudApiDialogHeaderSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlanIcon sx={cloudApiDialogHeaderIconSx} />
          Vender plano
        </Box>
        <IconButton onClick={handleClose} disabled={saving} aria-label="Fechar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={cloudApiDialogContentSx}>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          {patientLocked ? (
            <TextField
              size="small"
              fullWidth
              label="Paciente"
              value={patient?.full_name || initialPatient?.full_name || '—'}
              disabled
            />
          ) : (
            <Autocomplete
              options={patientOptions}
              loading={patientSearchLoading}
              value={patient}
              onChange={(_, v) => setPatient(v)}
              onInputChange={(_, v) => searchPatients(v)}
              getOptionLabel={(o) => o?.full_name || ''}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              renderInput={(params) => (
                <TextField {...params} size="small" label="Paciente" placeholder="Buscar por nome" />
              )}
            />
          )}

          <FormControl fullWidth size="small" disabled={catalogLoading}>
            <InputLabel>Plano</InputLabel>
            <Select
              label="Plano"
              value={procedureId}
              onChange={(e) => {
                setProcedureId(String(e.target.value));
                setDurationMonths('');
              }}
            >
              {catalog.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedProcedure && (
            <Typography variant="caption" color="text.secondary">
              {selectedProcedure.plan_weekly_frequency
                ? `${selectedProcedure.plan_weekly_frequency}x/semana`
                : null}
              {selectedProcedure.plan_sessions_per_cycle
                ? ` · ${selectedProcedure.plan_sessions_per_cycle} sessões/mês`
                : ' · sessões conforme agendamento'}
            </Typography>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth size="small" disabled={!procedureId}>
              <InputLabel>Vigência</InputLabel>
              <Select
                label="Vigência"
                value={durationMonths === '' ? '' : durationMonths}
                onChange={(e) => setDurationMonths(Number(e.target.value))}
              >
                {durationOptions.map((o) => (
                  <MenuItem key={o.months} value={o.months}>
                    {o.label || `${o.months} ${o.months === 1 ? 'mês' : 'meses'}`}
                    {' — '}
                    {formatCurrency(o.price)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              fullWidth
              type="date"
              label="Início da vigência"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Stack>

          {totalAmount > 0 && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Valor do contrato: <strong>{formatCurrency(totalAmount)}</strong>
            </Alert>
          )}

          {allowsInstallments && (
            <FormControl fullWidth size="small">
              <InputLabel>Forma de pagamento</InputLabel>
              <Select
                label="Forma de pagamento"
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value as 'installments' | 'upfront')}
              >
                <MenuItem value="installments">Mensalidades (uma cobrança por mês)</MenuItem>
                <MenuItem value="upfront">À vista (valor total na data de início)</MenuItem>
              </Select>
            </FormControl>
          )}

          {paymentMode === 'installments' && installmentPreview.length > 0 && (
            <>
              <FormControlLabel
                control={(
                  <Switch
                    checked={prorateFirst}
                    onChange={(e) => setProrateFirst(e.target.checked)}
                  />
                )}
                label="Ratear a primeira mensalidade (dias restantes do mês de início)"
              />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Parcela</TableCell>
                    <TableCell>Vencimento</TableCell>
                    <TableCell align="right">Valor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {installmentPreview.map((row) => (
                    <TableRow key={row.index}>
                      <TableCell>
                        {row.index}/{installmentPreview.length}
                        {row.proportional ? ' (proporcional)' : ''}
                      </TableCell>
                      <TableCell>{row.dueDate.split('-').reverse().join('/')}</TableCell>
                      <TableCell align="right">{formatCurrency(row.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          <FormControl fullWidth size="small">
            <InputLabel>Forma de recebimento prevista</InputLabel>
            <Select
              label="Forma de recebimento prevista"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(String(e.target.value))}
            >
              {paymentOptions.map((m) => (
                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Profissional responsável (opcional)</InputLabel>
            <Select
              label="Profissional responsável (opcional)"
              value={professionalId}
              onChange={(e) => setProfessionalId(String(e.target.value))}
            >
              <MenuItem value="">—</MenuItem>
              {professionals.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.full_name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={<Switch checked={autoRenew} onChange={(e) => setAutoRenew(e.target.checked)} />}
            label="Renovação automática ao fim da vigência"
          />

          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            label="Observações"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={cloudApiDialogFooterSx}>
        <Button onClick={handleClose} disabled={saving} sx={cloudApiDialogCancelButtonSx}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || catalogLoading}
          sx={cloudApiDialogPrimaryButtonSx}
        >
          {saving ? <CircularProgress size={22} color="inherit" /> : 'Confirmar venda'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function money2(value: unknown): number {
  return Math.round((Number(value) || 0) * 100) / 100;
}

export default SellPlanDialog;
